/* eslint-disable no-magic-numbers */
import {
  NonFinancialDriverNodeList,
  NonFinancialDriverType,
  NonFinancialDriverValues
} from '../../../predict/src/core/api/types/non-financial-drivers';

export interface DriverTreeNode {
  children: DriverTreeNode[];
  decimalDisplay: number;
  depth: number;
  id: string;
  is_excel_driver: boolean;
  isGroup: boolean;
  name: string;
  order: number;
  parentId?: string;
  pathName: string;
  type: NonFinancialDriverType;
  values: Record<string, number | null>;
}

export interface DriverTree {
  index: Record<string, DriverTreeNode>;
  tree: DriverTreeNode[];
}

/**
 * Normalises a date key to yyyy-mm format, accepting either yyyy-mm or yyyy-mm-dd input.
 */
const normalizeValueKey = (key: string): string | null => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
    return key.substring(0, 7);
  }
  if (/^\d{4}-\d{2}$/.test(key)) {
    return key;
  }
  return null;
};

/**
 * Combines a driver's values and order node into a single tree node.
 * Values are normalised to yyyy-mm keys with numeric (or null) amounts.
 */
const toTreeNode = (
  orderNode: NonFinancialDriverNodeList,
  data: NonFinancialDriverValues | undefined
): DriverTreeNode => ({
  children: [],
  decimalDisplay: orderNode.decimal_display,
  depth: 0,
  id: orderNode.node_id,
  is_excel_driver: orderNode.is_excel_driver,
  isGroup: orderNode.is_group,
  name: orderNode.name,
  order: orderNode.order,
  parentId: orderNode.parent_id ?? undefined,
  pathName: '',
  type: orderNode.driver_type,
  values: Object.entries(data?.values ?? {}).reduce<Record<string, number | null>>((acc, [key, value]) => {
    const normalized = normalizeValueKey(key);
    if (normalized) {
      acc[normalized] = value ? parseFloat(value) : null;
    }
    return acc;
  }, {})
});

/**
 * Recursively sets depth and pathName on each node in the tree.
 */
const annotate = (nodes: DriverTreeNode[], depth = 0, path = '') => {
  nodes.forEach(node => {
    node.depth = depth;
    node.pathName = path ? `${path} > ${node.name}` : node.name;
    if (node.children.length) {
      annotate(node.children, depth + 1, node.pathName);
    }
  });
};

/**
 * Recursively sorts a tree's nodes by their order property.
 */
const sortNodes = (nodes: DriverTreeNode[]) => {
  nodes.sort((a, b) => a.order - b.order);
  nodes.forEach(node => sortNodes(node.children));
};

/**
 * Combines driversData (values) and driversOrder (structure) into a sorted, annotated tree.
 * Returns both the nested tree and a flat index keyed by driver id.
 *
 * @param driversData - Flat list of driver values from the API
 * @param driversOrder - Ordered node list describing the tree structure and driver metadata
 */
export const buildDriverTree = (
  driversOrder: NonFinancialDriverNodeList[],
  driversData: NonFinancialDriverValues[] = []
): DriverTree => {
  const valuesById = new Map(driversData.map(d => [d.id, d]));
  const index: Record<string, DriverTreeNode> = {};

  driversOrder.forEach(orderNode => {
    index[orderNode.node_id] = toTreeNode(orderNode, valuesById.get(orderNode.node_id));
  });

  const tree: DriverTreeNode[] = [];
  driversOrder.forEach(({ node_id, parent_id }) => {
    const node = index[node_id];
    if (!node) {
      return;
    }
    const parent = parent_id ? index[parent_id] : null;
    (parent?.children ?? tree).push(node);
  });

  sortNodes(tree);
  annotate(tree);

  return { index, tree };
};
